import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore, useCartStore } from '@restaurant/core'
import {
  createOffersViewModel,
  createWebPlatformServices,
  WebOffersService
} from '@restaurant/viewmodels'
import type { OffersViewModel } from '@restaurant/viewmodels'
import { safeGetLogger } from '@restaurant/logging'

/**
 * Web-specific OffersViewModel shape. The web adapter auto-supplies the current
 * user's id, so it exposes userId-less validateOffer/claimOffer overrides instead
 * of the base OffersViewModel signatures (which require an explicit userId).
 */
export type WebOffersViewModel = Omit<OffersViewModel, 'validateOffer' | 'claimOffer'> & {
  validateOffer: (orderAmount: number) => Promise<void>
  claimOffer: () => Promise<void>
}


/**
 * Web platform adapter for OffersViewModel
 * 
 * Integrates the platform-agnostic OffersViewModel with React Router navigation
 * and web-specific services. Provides SessionViewModel integration for automatic
 * user context management.
 * 
 * Features:
 * - React Router integration for navigation
 * - Automatic user ID extraction from auth store
 * - Web-specific offers service integration  
 * - Enhanced offer claiming with user context
 * - Browser storage integration for offer persistence
 * 
 * Usage:
 * ```tsx
 * const offersViewModel = useWebOffersViewModel()
 * 
 * // Load specific offer
 * useEffect(() => {
 *   offersViewModel.loadOffer('offer-123')
 * }, [])
 * 
 * // Use with SheetContentOffer component
 * <SheetContentOffer {...offersViewModel.getSheetContentOfferProps()} />
 * ```
 */
export const useWebOffersViewModel = (): WebOffersViewModel => {
  const navigate = useNavigate()
  const location = useLocation()
  const authStore = useAuthStore()

  // Memoize platform services to prevent recreation on every render
  const platformServices = useMemo(
    () => createWebPlatformServices(navigate, location),
    [navigate, location]
  )

  // Memoize offers service to prevent recreation on every render
  const offersService = useMemo(() => new WebOffersService(), [])

  // Memoize ViewModel hook factory to prevent recreation on every render
  const OffersViewModelHook = useMemo(
    () => createOffersViewModel(platformServices, { offersService }),
    [platformServices, offersService]
  )

  const viewModel = OffersViewModelHook()
  const userId = authStore.user?.id

  // Memoize overrides to prevent new object on every render
  const validateOfferOverride = useCallback(async (orderAmount: number) => {
    if (!userId) {
      throw new Error('User must be logged in to validate offers')
    }
    return viewModel.validateOffer(userId, orderAmount)
  }, [viewModel.validateOffer, userId])

  const claimOfferOverride = useCallback(async () => {
    if (!userId) {
      throw new Error('User must be logged in to claim offers')
    }
    return viewModel.claimOffer(userId)
  }, [viewModel.claimOffer, userId])

  const getSheetContentOfferPropsOverride = useCallback(() => {
    const baseProps = viewModel.getSheetContentOfferProps()

    // If offer is loaded and user is authenticated, enhance the onOfferPress
    if (viewModel.offer && userId) {
      // Enhance offer claiming logic with user context
      if (viewModel.canClaimOffer && baseProps.offerDetailsProps?.onOfferPress) {
        baseProps.offerDetailsProps.onOfferPress = async () => {
          try {
            await viewModel.claimOffer(userId)
          } catch (error) {
            safeGetLogger()?.error('Offer claim failed', { domain: 'offers', error: String(error) })
            platformServices.platformService.showToast?.(
              'Failed to claim offer. Please try again.'
            )
          }
        }
      }

      // If user needs to validate offer first, set up validation
      if (!viewModel.validationResult && baseProps.offerDetailsProps?.buttonText === 'View Offer') {
        baseProps.offerDetailsProps.buttonText = 'Check Eligibility'
        baseProps.offerDetailsProps.onOfferPress = async () => {
          try {
            const cartTotal = useCartStore.getState().subtotal || 0.01
            await viewModel.validateOffer(userId, cartTotal)
          } catch (error) {
            safeGetLogger()?.error('Offer validation failed', { domain: 'offers', error: String(error) })
            platformServices.platformService.showToast?.(
              'Failed to check offer eligibility.'
            )
          }
        }
      }
    }

    // If user is not authenticated, provide login prompt
    if (!userId && viewModel.offer) {
      baseProps.offerDetailsProps = {
        ...baseProps.offerDetailsProps,
        buttonText: 'Login to Claim',
        disabled: false,
        onOfferPress: () => {
          platformServices.navigationService.navigateToLogin()
        },
      }
    }

    return baseProps
  }, [viewModel, userId, platformServices])

  // Memoize the enhanced ViewModel to prevent new object reference on every render
  return useMemo(() => ({
    ...viewModel,
    validateOffer: validateOfferOverride,
    claimOffer: claimOfferOverride,
    getSheetContentOfferProps: getSheetContentOfferPropsOverride,
  }), [viewModel, validateOfferOverride, claimOfferOverride, getSheetContentOfferPropsOverride])
}

/**
 * Convenience hook for offer claiming workflow
 * 
 * Provides a simplified interface for common offer operations with automatic
 * user context management and error handling.
 */
export const useOfferClaimingWorkflow = () => {
  const offersViewModel = useWebOffersViewModel()
  const authStore = useAuthStore()

  /**
   * Complete offer claiming workflow
   * @param offerId - The offer to claim
   * @param cartTotal - Current cart total for validation
   */
  const claimOfferWorkflow = async (offerId: string, cartTotal: number = 0) => {
    try {
      // Ensure user is authenticated
      if (!authStore.user) {
        throw new Error('User must be logged in to claim offers')
      }

      // Load offer details
      await offersViewModel.loadOffer(offerId)

      // Validate offer if cart total provided
      if (cartTotal > 0) {
        await offersViewModel.validateOffer(cartTotal)
        
        // Check if offer is valid before claiming
        if (!offersViewModel.canClaimOffer) {
          throw new Error('Offer is not eligible for your current cart')
        }
      }

      // Claim the offer
      await offersViewModel.claimOffer()

      // Apply to cart if successfully claimed
      if (offersViewModel.claimResult?.success) {
        await offersViewModel.applyOfferToCart()
      }

      return {
        success: true,
        discountApplied: offersViewModel.applyResult?.discountApplied || 0,
      }
    } catch (error) {
      safeGetLogger()?.error('Offer claiming workflow failed', { domain: 'offers', error: String(error) })
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  return {
    offersViewModel,
    claimOfferWorkflow,
    isAuthenticated: !!authStore.user,
  }
}